// A player's page, as data. P6-329c (GN-2c).
//
// `playerModel` turns what the readers gave (one player file, maybe a second season's file, maybe the index entry, maybe
// the leaders file) into the plain object the page draws. It is pure, and it holds every rule the design states about
// WHAT the page says, so each is asserted as a value: which numbers are headline, when there is a comparison, which
// percentile bars are drawn, which columns the splits have.
//
// ═══════════════════════════════════════════════════════════════════════════
// MISSING PARTS ARE NOT DRAWN, AND THEY ARE NOT DRAWN AS EMPTY PLACES
// ═══════════════════════════════════════════════════════════════════════════
//
// The design says it in one sentence: *"Missing parts aren't drawn."* No game log means no trend chart (the backfill
// stores logs for the newest season only, so 2024-25 has none). Only two splits in the file means two columns. One
// stint means no stints card. A player held only in the index gets the hero and his league ranks and nothing else.
// There are no skeletons and no placeholders, so every section below is `null` or `[]` when its data is absent, and a
// component that draws a heading for an empty one is a bug a test can see.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE COMPARISON NEEDS BOTH SEASONS' FILES, AND ONLY THEN IS IT DRAWN
// ═══════════════════════════════════════════════════════════════════════════
//
// Each headline stat carries the other season's value and the difference (`24-25 28.2` over `+5.3`). It exists only
// when this season's file AND the other's are in hand: with one, there is no second number and the line is not
// drawn, and it is never drawn against a number that came from somewhere else (the leaders file's value for a player
// whose own file is absent is a different measurement). A rookie has one season, so he has none.
//
// ═══════════════════════════════════════════════════════════════════════════
// A PERCENTILE IS DRAWN ONLY FOR A STAT HE QUALIFIED FOR
// ═══════════════════════════════════════════════════════════════════════════
//
// The file carries a percentile for every stat and, beside it, whether he met the minimum for that stat
// (`qualified`). Luka Doncic's 2024-25 file has `pts: 98.7` and `qualified.pts: false` (50 games of the 58 it takes):
// the number is real and the bar would claim he ranked 98.7th among players he is not being ranked against. So a
// stat that is not qualified says `Not qualified`, and, for a counting stat when the leaders file gives the minimum,
// how close he was (`Not qualified · 50 of 58 games`). A percentage qualifies on makes and not on games, so it never
// says the games.

import {
  CAT_NAME,
  LEAD_CATS,
  PCT_KEYS,
  PICKS,
  STAT,
  dayMon,
  fmt,
  fmtDelta,
  int,
  isNumber,
  isPct,
  monthName,
  ord,
  shortSeason,
  wdDayMon,
  wl,
} from './historyFormat.js'
import { monthLabels, niceTicks, sparkline } from './chartMath.js'

const HEADLINE_KEYS = ['pts', 'reb', 'ast', 'fg_pct', 'fg3_pct', 'ft_pct', 'stl', 'blk', 'min']
const TOTAL_KEYS = ['pts', 'reb', 'ast', 'fg3m', 'stl', 'blk', 'tov', 'min']

/** The columns of a splits table: home and road always, neutral only when the file has a neutral object. */
const SPLIT_ROWS = [
  ['GP', (x) => String(x.gp)],
  ['W–L', (x) => wl(x.w, x.l)],
  ['MIN', (x) => fmt('min', x.min)],
  ['PTS', (x) => fmt('pts', x.pts)],
  ['REB', (x) => fmt('reb', x.reb)],
  ['AST', (x) => fmt('ast', x.ast)],
  ['FG%', (x) => fmt('fg_pct', x.fg_pct)],
  ['3P%', (x) => fmt('fg3_pct', x.fg3_pct)],
  ['FT%', (x) => fmt('ft_pct', x.ft_pct)],
  ['+/−', (x) => fmt('plus_minus', x.plus_minus)],
]

/** A player's stints in date order, or `null` for a file with none. */
export function stintsOf(file) {
  const teams = file?.teams
  if (!Array.isArray(teams) || teams.length === 0) return null
  return [...teams].sort((a, b) => (a.from < b.from ? -1 : 1))
}

/** Where a leaders file ranks this player: one `{ cat, rank, value, last10 }` per category he is in the top ten of. */
export function ranksOf(leaders, personId) {
  const ranks = []
  for (const cat of LEAD_CATS) {
    const row = (leaders?.categories?.[cat] ?? []).find((entry) => entry?.player_id === personId)
    if (row) ranks.push({ cat, rank: row.rank, value: row.value, last10: row.last10 ?? null })
  }
  return ranks
}

/**
 * The percentile rows: one per stat the file has a percentile or a qualification for, in the card's order.
 *
 * `qualified` is true only when the file says he qualified AND carries a percentile; the bar is drawn only then. A
 * stat he did not qualify for is a row with `why` and no bar.
 */
export function percentileRows(file, leaders) {
  if (!file?.percentiles) return []
  const qualifiedFor = file.qualified ?? {}
  return PCT_KEYS.filter((key) => file.percentiles[key] != null || key in qualifiedFor).map((key) => {
    const qualified = Boolean(qualifiedFor[key]) && file.percentiles[key] != null
    const minGames = leaders?.min_gp
    const why =
      !isPct(key) && isNumber(minGames) && file.gp < minGames
        ? `Not qualified · ${file.gp} of ${minGames} games`
        : 'Not qualified'
    return {
      key,
      label: STAT[key],
      value: fmt(key, file.per_game?.[key]),
      qualified,
      width: qualified ? file.percentiles[key] : 0,
      percentile: qualified ? file.percentiles[key].toFixed(1) : '',
      pool: file.percentile_pools?.[key] ?? null,
      why,
    }
  })
}

/** The splits table, or `null` when the file lacks a home or a road split. */
export function splitsModel(file) {
  const splits = file?.splits
  if (!splits?.home || !splits?.road) return null
  const columns = [
    ['home', 'Home'],
    ['road', 'Road'],
    ...(splits.neutral && typeof splits.neutral === 'object' ? [['neutral', 'Neutral']] : []),
  ]
  const neutralGames = isNumber(splits.neutral_games) ? splits.neutral_games : 0
  const note =
    columns.length > 2 && neutralGames > 0
      ? `Neutral: ${neutralGames} game${neutralGames > 1 ? 's' : ''} at a third city, away from both teams’ arenas.`
      : null
  return {
    title: columns.length > 2 ? 'Home · road · neutral' : 'Home · road',
    heads: columns.map(([, label]) => label),
    columns: columns.length,
    rows: SPLIT_ROWS.map(([label, text]) => ({
      label,
      cells: columns.map(([key]) => (splits[key] ? text(splits[key]) : '')),
    })),
    note,
  }
}

const dayNumber = (isoDate) => new Date(`${isoDate}T12:00:00Z`).getTime()

/** The stints card: the bar of segments, its two ends, and one row per stint. `null` for fewer than two stints. */
export function stintsModel(stints, nameFor) {
  if (!stints || stints.length < 2) return null
  const first = dayNumber(stints[0].from)
  const last = dayNumber(stints[stints.length - 1].to)
  const span = last - first || 1
  const segments = stints.map((stint) => {
    const from = ((dayNumber(stint.from) - first) / span) * 100
    const to = ((dayNumber(stint.to) - first) / span) * 100
    return { left: from, width: Math.max(2, to - from), abbr: stint.abbr, teamId: stint.team_id }
  })
  return {
    segments,
    start: `${dayMon(stints[0].from)} ${stints[0].from.slice(0, 4)}`,
    end: `${dayMon(stints[stints.length - 1].to)} ${stints[stints.length - 1].to.slice(0, 4)}`,
    count: `${stints.length} teams · ${stints.reduce((sum, stint) => sum + stint.gp, 0)} GP`,
    rows: stints.map((stint, index) => ({
      n: index + 1,
      abbr: stint.abbr,
      teamId: stint.team_id,
      name: nameFor(stint),
      range: `${dayMon(stint.from)} – ${dayMon(stint.to)}`,
      gp: stint.gp,
    })),
  }
}

/** One game-log value of a stat: `fg_pct` is made over attempted, anything else is the column. `null` where it has no value. */
function gameValue(row, column, key) {
  if (key === 'fg_pct') return row[column.fga] ? row[column.fgm] / row[column.fga] : null
  const value = row[column[key]]
  return isNumber(value) ? value : null
}

/** What a game's value is printed as in the readout: a percentage to one decimal, minutes rounded, `+/−` signed. */
function gameText(key, value) {
  if (value === null || value === undefined) return '–'
  if (isPct(key)) return (value * 100).toFixed(1)
  if (key === 'min') return String(Math.round(value))
  if (key === 'plus_minus') return `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value)}`
  return String(value)
}

/** An axis label: percentages as whole numbers, `+/−` with its sign, negatives with the true minus. */
function tickLabel(key, tick) {
  if (isPct(key)) return String(Math.round(tick * 100))
  return `${key === 'plus_minus' && tick > 0 ? '+' : ''}${tick < 0 ? `−${Math.abs(tick)}` : tick}`
}

/** The game log's columns by name. */
function columnsOf(log) {
  const column = {}
  log.columns.forEach((name, index) => {
    column[name] = index
  })
  return column
}

/**
 * The game-by-game chart for one stat: a bar per game, the axis, the season average, the months under it, and the
 * readout for the selected game. `selected` is an index, or `null` for the default, which is the season high.
 *
 * `teamAt(date)` names the team he played for on that date (a traded player's bars change colour at the trade), as an
 * abbreviation, and the bar carries it so the component can colour it from the board's table.
 */
export function trendModel({ file, stat, selected, teamAt }) {
  const log = file?.game_log
  if (!log || !Array.isArray(log.rows) || log.rows.length === 0 || !Array.isArray(log.columns)) return null
  const column = columnsOf(log)
  const rows = log.rows
  const count = rows.length
  const key = PICKS.includes(stat) ? stat : 'pts'
  const values = rows.map((row) => gameValue(row, column, key))
  const measured = values.filter((value) => value !== null)
  const average = file.per_game?.[key]
  const axis = isPct(key)
    ? { lo: 0, hi: 1, ticks: [0, 0.25, 0.5, 0.75, 1] }
    : niceTicks(Math.min(0, ...measured), Math.max(...measured, isNumber(average) ? average : 0), 4)
  const span = axis.hi - axis.lo
  const y = (value) => ((axis.hi - value) / span) * 100
  const slot = 100 / count

  // The season high is the FIRST game with the biggest value, which is where the readout opens.
  let high = 0
  values.forEach((value, index) => {
    if (value !== null && (values[high] === null || value > values[high])) high = index
  })
  const index = selected === null || selected === undefined ? high : Math.max(0, Math.min(count - 1, selected))

  const bars = []
  rows.forEach((row, i) => {
    const value = values[i]
    if (value === null) return
    bars.push({
      index: i,
      x: i * slot,
      width: slot,
      top: value >= 0 ? y(value) : y(0),
      height: Math.max(0.8, Math.abs(y(value) - y(0))),
      win: row[column.wl] === 'W',
      team: teamAt(row[column.date]),
      selected: i === index,
    })
  })

  const row = rows[index]
  const loc = row[column.loc]
  const where = loc === 'A' ? `@ ${row[column.opp]}` : `vs ${row[column.opp]}${loc === 'N' ? ' · neutral' : ''}`
  const sub = `${wdDayMon(row[column.date])} · ${where} · ${row[column.wl]}`
  const readValue = gameText(key, values[index])
  return {
    key,
    count,
    first: dayMon(rows[0][column.date]),
    last: dayMon(rows[count - 1][column.date]),
    countLine: `${count} games · ${dayMon(rows[0][column.date])} – ${dayMon(rows[count - 1][column.date])}`,
    selected: index,
    high,
    bars,
    ticks: axis.ticks.map((tick) => ({ top: y(tick), label: tickLabel(key, tick) })),
    zero: y(0),
    average: isNumber(average) ? y(average) : null,
    averageLabel: `Season avg ${fmt(key, average)}`,
    months: monthLabels(
      rows.map((r) => r[column.date]),
      (i) => i * slot,
      monthName,
    ),
    readKicker: index === high ? `Season high · game ${index + 1} of ${count}` : `Game ${index + 1} of ${count}`,
    readValue,
    readUnit: STAT[key],
    readSub: sub,
    readLine: `${row[column.pts]} PTS · ${row[column.reb]} REB · ${row[column.ast]} AST · ${row[column.fgm]}-${row[column.fga]} FG`,
    valueText: `Game ${index + 1}, ${readValue} ${STAT[key]}, ${sub}`,
    label: `${STAT[key]} by game. Use arrow keys to move between games.`,
    lastTeam: teamAt(rows[count - 1][column.date]),
  }
}

/**
 * Everything a player's page draws, or `null` for a player with no record at all.
 *
 * - `file`      this season's player file, or `null`
 * - `other`     `{ season, file }` for the season he is compared with, or `null`
 * - `entry`     his entry in this season's index, or `null`
 * - `leaders`   this season's leaders file, or `null`
 * - `nameFor`   `(stint) => the team's full name`, which the board's table answers (or the abbreviation)
 */
export function playerModel({ season, personId, file, other, entry, leaders, nameFor }) {
  if (!file && !entry) return null
  const stints = stintsOf(file)
  const abbrs = stints ? stints.map((stint) => stint.abbr) : [entry?.team ?? file?.team]
  const ranks = ranksOf(leaders, personId)
  const leaderValue = {}
  for (const rank of ranks) leaderValue[rank.cat] = rank.value
  const perGame = file ? file.per_game : null
  const valueOf = (key) => (perGame ? perGame[key] : leaderValue[key])
  const comparison = file && other?.file?.per_game ? other : null

  const cell = (key) => {
    const value = valueOf(key)
    const before = comparison ? comparison.file.per_game[key] : null
    const has = before != null && value != null
    return {
      key,
      label: STAT[key],
      value: fmt(key, value),
      comparison: has ? `${shortSeason(comparison.season)} ${fmt(key, before)}` : null,
      delta: has ? fmtDelta(key, value - before) : null,
    }
  }
  const keys = HEADLINE_KEYS.filter((key) => valueOf(key) != null)
  const bio = file?.bio ?? null
  const name = file?.name ?? entry.name
  const traded = Boolean(file?.traded)

  const meta = [
    stints && stints.length > 1 ? abbrs.join(' → ') : (entry?.team ?? file?.team),
    bio?.jersey ? `#${bio.jersey}` : null,
    bio?.position ?? entry?.pos,
    bio?.height,
    bio?.weight ? `${bio.weight} lb` : null,
    file?.age ? `Age ${file.age}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const totals = file?.totals
    ? TOTAL_KEYS.filter((key) => file.totals[key] != null).map((key) => ({ key, label: STAT[key], value: int(file.totals[key]) }))
    : []

  const rankRows = file
    ? []
    : ranks
        .map((rank) => ({
          cat: rank.cat,
          label: CAT_NAME[rank.cat],
          sub: `${ord(rank.rank)} in the league`,
          value: fmt(rank.cat, rank.value),
          spark: sparkline(rank.last10 ?? [], rank.value),
        }))
        .filter((row) => !row.spark.empty)

  return {
    personId,
    season,
    name,
    meta,
    teamIds: stints ? stints.map((stint) => stint.team_id) : [entry?.team_id ?? file?.team_id],
    abbrs,
    big: keys.slice(0, 3).map(cell),
    small: keys.slice(3).map(cell),
    gpLine: file ? `${file.gp} GP · ${wl(file.w, file.l)} in his games` : entry?.gp ? `${entry.gp} GP` : '',
    chips: [
      ...ranks
        .slice()
        .sort((a, b) => a.rank - b.rank)
        .slice(0, 4)
        .map((rank) => `${ord(rank.rank)} ${STAT[rank.cat]}`),
      ...(traded ? ['Traded'] : []),
    ],
    hasFile: Boolean(file),
    hasComparison: comparison !== null,
    asOf: file?.as_of ?? leaders?.as_of ?? null,
    source: file?.source ?? leaders?.source ?? null,
    rankRows,
    totals,
    percentiles: percentileRows(file, leaders),
    splits: splitsModel(file),
    stints: stintsModel(stints, nameFor),
    stintList: stints,
    bio: bio
      ? [
          bio.draft ? `Drafted ${bio.draft.year} · round ${bio.draft.round}, pick ${bio.draft.number}` : null,
          bio.college,
          bio.country,
        ]
          .filter(Boolean)
          .join(' · ')
      : '',
  }
}
